import React, { useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authenticateFromMagicLink } from '@src/apis/reware';
import { completeAuthentication } from '@src/providers/authSession';
import { getSignInErrorMessage } from '@src/providers/authErrors';
import { clearSession } from '@src/providers/authProvider';

/*
 * /magic-link callback — the destination of the emailed sign-in link (same-device path). Reads the
 * single-use token from the URL, exchanges it for a session, then populates sessionStorage via the
 * shared completeAuthentication helper and redirects to the app (or partner selection).
 */
export const MagicLinkCallback = () => {
  const notify = useNotify();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // The token is single-use; guard against React StrictMode's double-invocation so we don't
  // consume it twice (the second exchange would fail).
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    const processCallback = async () => {
      // The token arrives in the URL *fragment* (`#token=…`), not the query string, so it is never
      // sent to the server/CDN on the initial page load; read it client-side here.
      const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token');
      // Scrub the token from the address bar immediately so it doesn't linger in browser history
      // or leak via the Referer header on requests this page makes.
      if (window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      try {
        if (!token) {
          throw new Error('Missing sign-in token in link');
        }

        const user = await authenticateFromMagicLink({ token });
        const { redirectTo } = await completeAuthentication(user);

        setStatus('success');
        setTimeout(() => {
          window.location.replace(redirectTo);
        }, 500);
      } catch (error) {
        console.error('Magic-link callback processing failed:', error);
        // The token is already consumed by the time completeAuthentication runs, so this link can't
        // be retried. completeAuthentication is transactional, but clear defensively so an
        // unexpected throw can never strand a partial session.
        clearSession();

        const message = getSignInErrorMessage(
          error,
          'This sign-in link is invalid or has expired.',
        );
        setErrorMessage(message);
        setStatus('error');
        notify(message, { type: 'error' });

        // Both a permissions problem and a retryable auth failure send the user back to
        // /login
        setTimeout(() => {
          window.location.replace('/login');
        }, 3000);
      }
    };

    void processCallback();
  }, [notify]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      {status === 'processing' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Signing you in...
          </Typography>
        </Box>
      )}

      {status === 'success' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            Signed in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to dashboard...
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error.main">
            Sign-in failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting so you can request a new link...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
